import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { SessionService } from './session.service';

/**
 * Nightly cleanup of dead login sessions.
 *
 * admin.user_sessions is append-heavy by design: every refresh rotates, which
 * writes a new row and revokes the previous one rather than deleting it. Rows
 * therefore accumulate per REFRESH, not per login — roughly 30 a day for a
 * user active eight hours with a 15-minute access token, before counting
 * devices. Nothing else removes them, so without this job the table grows
 * without bound.
 *
 * Runs at 03:30, deliberately offset from ImageTrashPurgeService's 03:00 so
 * two retention sweeps are not competing for the same database at the same
 * moment.
 */
@Injectable()
export class SessionPruneService {
  private readonly logger = new Logger(SessionPruneService.name);

  constructor(private readonly sessions: SessionService) {}

  @Cron('30 3 * * *')
  async pruneDeadSessions(): Promise<void> {
    try {
      const removed = await this.sessions.prune();

      // Only log when something happened — a nightly "removed 0 sessions"
      // line is noise that trains people to ignore the job's output.
      if (removed > 0) {
        this.logger.log(`Pruned ${removed} expired/revoked session(s)`);
      }
    } catch (error) {
      // Housekeeping must never take the API down. A failed prune means the
      // table keeps growing for another day, which is recoverable; an
      // unhandled rejection in a scheduled task is not.
      this.logger.error(`Session prune failed: ${(error as Error).message}`);
    }
  }
}
